// The weekly scroll's sheet on the web (owner, 2026-10-05: "The weekly scroll should come up on next
// view of the app each week, similar to the what's new, and it should take priority. After seen it is
// on the notice board."). The rule is shared/weekly.ts (weeklyOnOpen, weeklySheetShows,
// whatsNewWaitsForWeekly, weeklyOnBoard); this module keeps the device's bookmark (`op.weeklySeen`,
// the newest week it has shown) and, the way src/whats-new.ts does, decides once per page load
// whether the sheet (src/components/WeeklyScroll.tsx WeeklySheet) has a scroll to show.
//
// Started by the app shell once signed in with `weeklyUI` on (it reads the newest scroll); the open
// board says when it has loaded; the shell shows the sheet before What's new, which waits while the
// scroll is due; closing bookmarks the week, and from then the open board carries the scroll. A
// brand new device bookmarks the week silently and shows nothing, as What's new does. A newer week
// read later by the board (offerWeekly) comes up the same way.
import { useEffect, useState } from "preact/hooks";
import { WEEKLY_SEEN_KEY, laterWeek, parseWeeklySeen, weeklyOnOpen, type WeeklyScroll } from "../shared/weekly.ts";
import { existingDevice } from "./whats-new.ts";

/** Kept when storage refuses, so a private window does not show the sheet twice in one open. */
let memory: string | null = null;

/** The newest week this device has shown, or null for none. */
export function weeklyBookmark(): string | null {
  try {
    return parseWeeklySeen(localStorage.getItem(WEEKLY_SEEN_KEY)) ?? memory;
  } catch {
    return memory;
  }
}

/** Moves the bookmark up to `week` (never down). */
export function bookmarkWeekly(week: string): void {
  const next = laterWeek(weeklyBookmark(), week)!;
  memory = next;
  try {
    localStorage.setItem(WEEKLY_SEEN_KEY, next);
  } catch {
    /* ignore: the copy in memory holds for this open */
  }
}

// ---------- this page load ----------

/** Whether the device was in use when this open started (the What's new rule), decided at start. */
let existing: boolean | null = null;
let reading = false;
let pending: WeeklyScroll | null = null;
let boardLoaded = false;
/** The workshop's "Show the scroll now": shown at once on this device, never bookmarked. */
let preview: WeeklyScroll | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const fn of listeners) fn();
}

/** A scroll read (at start, or by the board later): bookmark it silently on a brand new device, else queue it if it is newer than the bookmark. */
export function offerWeekly(scroll: WeeklyScroll | null): void {
  if (existing === null) existing = existingDevice();
  const { show, bookmarkNow } = weeklyOnOpen(weeklyBookmark(), existing, scroll);
  if (bookmarkNow !== null) bookmarkWeekly(bookmarkNow);
  if (show && show.week !== pending?.week) pending = show;
  emit();
}

/** Signed in with `weeklyUI` on: read the newest scroll once per page load. What's new waits while it is read. */
export function startWeekly(load: () => Promise<WeeklyScroll | null>): void {
  if (existing !== null || reading) return;
  existing = existingDevice();
  reading = true;
  emit();
  load()
    .then(offerWeekly, () => {
      /* offline: nothing to show this open */
    })
    .finally(() => {
      reading = false;
      emit();
    });
}

/** The open board has its list (src/views/Board.tsx): the sheet may show from now on. */
export function weeklyBoardLoaded(): void {
  if (boardLoaded) return;
  boardLoaded = true;
  emit();
}

/** Whether the scroll is still to come this open (being read, or queued and not closed): What's new waits. */
export function weeklyDue(): boolean {
  return reading || pending !== null;
}

/** The scroll the sheet may show now: queued, once the board has loaded. */
export function pendingWeekly(): WeeklyScroll | null {
  return preview ?? (boardLoaded ? pending : null);
}

/**
 * The workshop's "Show the scroll now" (shared/weekly.ts previewScroll): the sheet comes up at once on
 * this device with the scroll as it would read now, flag or no flag. Closing it bookmarks nothing, so
 * Sunday's scroll still comes up; nothing is posted and no other device sees it.
 */
export function previewWeekly(scroll: WeeklyScroll): void {
  preview = scroll;
  emit();
}

/** Whether the sheet up (or about to come up) is the workshop's preview. */
export function previewingWeekly(): boolean {
  return preview !== null;
}

/** The sheet was closed (Got it, the dim, Escape): bookmark its week; the open board carries it from now. */
export function closeWeekly(): void {
  if (preview) {
    // A preview closes without a bookmark; a real scroll still queued shows next.
    preview = null;
    emit();
    return;
  }
  if (!pending) return;
  bookmarkWeekly(pending.week);
  pending = null;
  emit();
}

export interface WeeklySheetState {
  /** The scroll to show in the sheet now, or null. */
  scroll: WeeklyScroll | null;
  /** Still to come this open: What's new waits. */
  due: boolean;
  /** The device's bookmark: the open board carries a scroll whose week it has reached. */
  seen: string | null;
  /** The sheet is the workshop's preview: it shows whatever the flag says. */
  preview: boolean;
}

function read(): WeeklySheetState {
  return { scroll: pendingWeekly(), due: weeklyDue(), seen: weeklyBookmark(), preview: previewingWeekly() };
}

/** The sheet's state for the shell and the board. */
export function useWeeklySheet(): WeeklySheetState {
  const [state, setState] = useState(read);
  useEffect(() => {
    const update = () =>
      setState((prev) => {
        const next = read();
        return prev.scroll === next.scroll && prev.due === next.due && prev.seen === next.seen && prev.preview === next.preview ? prev : next;
      });
    listeners.add(update);
    update();
    return () => {
      listeners.delete(update);
    };
  }, []);
  return state;
}

/** Tests only. */
export function resetWeeklyForTests(): void {
  existing = null;
  reading = false;
  pending = null;
  preview = null;
  boardLoaded = false;
  memory = null;
  listeners.clear();
}
